import React from 'react';
import StaticPageLayout from '../components/StaticPageLayout';

const ManuscriptPreparationPage = () => (
  <StaticPageLayout
    overline="Submission Policies"
    title="Manuscript Preparation"
    description="Guidelines for formatting and preparing your manuscript prior to submission. Following these specifications ensures a smooth editorial and review process."
    breadcrumbs={[{ label: 'Submission Policies', path: '/submission-process' }, { label: 'Manuscript Preparation', path: '/manuscript-preparation' }]}
  >
    <div className="space-y-10">
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">File Format Requirements</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'Accepted Formats', value: 'PDF (preferred), DOCX, DOC' },
            { label: 'Maximum File Size', value: '20 MB per submission' },
            { label: 'Supplementary Files', value: 'Up to 5 files; labelled clearly' },
            { label: 'Page Limit', value: 'No strict limit; conciseness encouraged' },
          ].map(item => (
            <div key={item.label} className="border border-[#E5E7EB] bg-[#F5F7FA] px-4 py-3 flex justify-between items-center text-sm">
              <span className="text-[#6B7280]">{item.label}</span>
              <span className="font-medium text-[#1F1F1F]">{item.value}</span>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Formatting Standards</h2>
        <div className="space-y-3">
          {[
            { title: 'Font & Spacing', desc: 'Use a standard serif or sans-serif font (e.g., Times New Roman 12pt or Arial 11pt) with double line spacing throughout the manuscript body.' },
            { title: 'Margins', desc: '2.5 cm margins on all sides. Pages should be numbered consecutively from the title page.' },
            { title: 'Headings', desc: 'Use a consistent hierarchical heading structure. Avoid more than three levels of heading.' },
            { title: 'Abstract', desc: 'Structured abstract of 150–250 words including: Objective, Methods, Results, and Conclusion. Do not include citations in the abstract.' },
            { title: 'Keywords', desc: 'Provide 4–8 keywords relevant to the manuscript. Avoid overly broad or generic terms.' },
          ].map(item => (
            <div key={item.title} className="border border-[#E5E7EB] p-4 bg-white">
              <h4 className="font-semibold text-[#1F1F1F] text-sm mb-1">{item.title}</h4>
              <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">References & Citations</h2>
        <p className="text-[#4B5563] leading-relaxed mb-3">
          Authors should use a consistent, widely recognised citation style appropriate to their discipline (e.g., APA, Vancouver, IEEE). All references cited in the text must appear in the reference list, and vice versa.
        </p>
        <p className="text-[#4B5563] leading-relaxed">
          Self-citations should be kept to a minimum and only included where genuinely relevant. Fabricated or inaccurate references constitute a form of research misconduct and are grounds for rejection or retraction.
        </p>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Figures & Tables</h2>
        <p className="text-[#4B5563] leading-relaxed mb-3">
          All figures and tables must be clearly labelled, numbered consecutively, and accompanied by descriptive captions. High-resolution images (minimum 300 DPI) are required for figures intended for publication.
        </p>
        <p className="text-[#4B5563] text-sm text-[#6B7280]">
          Where figures or tables contain data from third-party sources, appropriate attribution and permissions must be obtained and disclosed in the manuscript.
        </p>
      </section>
    </div>
  </StaticPageLayout>
);

export default ManuscriptPreparationPage;
